import { Koi } from '@ksyos/koi';
import type { AddressCountry } from '@common/enums';
import { PaymentMethod } from '@common/enums';
import type { ISchemaContainer } from './schemaContainer';
import { emailSchema } from './schemas/emailSchema';

export interface ICostInfoAndConsentState {
    emailAddress?: string | null;
    hasCostConsentPatient: boolean;
    homeAddress?: IHomeAddress;
    invoiceAddress?: IInvoiceAddress;
    paymentMethod: PaymentMethod;
}

export interface IAddressBase {
    houseNumber: string;
    houseNumberSuffix?: string;
    postalCode: string;
}

export type IHomeAddress = IAddressBase;

export interface IInvoiceAddress extends IAddressBase {
    city: string;
    country: AddressCountry;
    street: string;
}

export interface ICostInfoAndConsentValidated {
    consent: boolean;
    emailAddress: string | null;
    homeAddress?: IHomeAddress;
    invoiceAddress?: IInvoiceAddress;
    paymentMethod: PaymentMethod;
}

const addressBaseSchema = Koi.object({
    houseNumber: Koi.string().numeric().max(255),
    houseNumberSuffix: Koi.string().min(0).max(255).optional().allow(null),
    postalCode: Koi.string().zipCodeNL(),
});

const homeAddressSchema = addressBaseSchema;

const invoiceAddressSchema = addressBaseSchema.keys({
    city: Koi.string().max(255),
    country: Koi.string().max(255),
    street: Koi.string().max(255),
});

export const costInfoAndConsentSchema: ISchemaContainer<ICostInfoAndConsentValidated> = {
    schema: Koi.object({
        emailAddress: Koi.when('paymentMethod', {
            is: PaymentMethod.ideal,
            otherwise: Koi.forbidden(),
            then: emailSchema,
        }),
        hasCostConsentPatient: Koi.boolean().invalid(false),
        homeAddress: Koi.when('paymentMethod', {
            is: PaymentMethod.ideal,
            otherwise: Koi.forbidden(),
            then: homeAddressSchema,
        }),
        invoiceAddress: Koi.when('paymentMethod', {
            is: PaymentMethod.invoice,
            otherwise: Koi.forbidden(),
            then: invoiceAddressSchema,
        }),
        paymentMethod: Koi.koi().enum(PaymentMethod),
    }).options({ convert: false, presence: 'required' }),
};
